import { type ColumnPinningState, type Updater } from "@tanstack/react-table";
import { useMemo, useEffect } from "react";
import { useLocalStorage } from "react-use";

const defaultPinning: ColumnPinningState = { left: [], right: [] };

export type ColumnPinningOptions = {
  readonly alwaysPinnedRight?: readonly string[];
};

const ensureAlwaysPinnedRight = (
  pinning: ColumnPinningState,
  columnOrder: string[],
  alwaysPinnedRight: readonly string[],
): ColumnPinningState => {
  const currentRight = pinning.right ?? [];
  const columnsToAdd = alwaysPinnedRight.filter((col) => columnOrder.includes(col) && !currentRight.includes(col));
  return columnsToAdd.length > 0 ? { ...pinning, right: [...currentRight, ...columnsToAdd] } : pinning;
};

export function computeInitialPinning(
  storedPinning: ColumnPinningState | undefined,
  columnOrder: string[],
  options?: ColumnPinningOptions,
): ColumnPinningState {
  const alwaysPinnedRight = options?.alwaysPinnedRight ?? [];

  // If user has stored pinning (even empty arrays), respect their choice
  if (storedPinning !== undefined) {
    const left = storedPinning.left ?? [];
    const right = storedPinning.right ?? [];

    // Validate stored columns - if invalid, treat as empty
    const validLeft = left.length === 0 || (left[0] && columnOrder.includes(left[0])) ? left : [];
    const validRight = right.length === 0 || (right[0] && columnOrder.includes(right[0])) ? right : [];

    // Return user's choice, ensuring always-pinned columns are pinned right
    return ensureAlwaysPinnedRight({ left: validLeft, right: validRight }, columnOrder, alwaysPinnedRight);
  }

  // No stored pinning - apply defaults
  const rightPinned = alwaysPinnedRight.filter((col) => columnOrder.includes(col));
  if (rightPinned.length > 0) {
    return { left: [], right: [...rightPinned] };
  }
  const firstColumnId = columnOrder[0];
  return firstColumnId ? { left: [firstColumnId], right: [] } : defaultPinning;
}

export function computePinningUpdate(
  storedPinning: ColumnPinningState | undefined,
  columnOrder: string[],
  options?: ColumnPinningOptions,
) {
  const alwaysPinnedRight = options?.alwaysPinnedRight ?? [];
  const left = storedPinning?.left ?? [];
  const right = storedPinning?.right ?? [];

  if (left.length > 0) {
    const currentPinnedId = left[0];
    const newFirstColumnId = columnOrder[0];

    // If the pinned column is no longer first, update to pin the new first column
    if (
      newFirstColumnId &&
      currentPinnedId &&
      currentPinnedId !== newFirstColumnId &&
      columnOrder.includes(currentPinnedId)
    ) {
      return ensureAlwaysPinnedRight({ left: [newFirstColumnId], right }, columnOrder, alwaysPinnedRight);
    }
  } else if (!storedPinning) {
    // If no column was pinned and we haven't explicitly unpinned (storedPinning is undefined/null), pin the first column
    const firstColumnId = columnOrder[0];
    if (firstColumnId) {
      return ensureAlwaysPinnedRight({ left: [firstColumnId], right: [] }, columnOrder, alwaysPinnedRight);
    }
  }

  return null;
}

export const createPinningToggle = (
  columnId: string,
  isPinned: boolean,
): ((prev: ColumnPinningState) => ColumnPinningState) =>
  isPinned ? (prev) => ({ ...prev, left: [] }) : (prev) => ({ ...prev, left: [columnId] });

export function useTableColumnPinning(tableId: string, columnOrder: string[], options?: ColumnPinningOptions) {
  const [storedPinning, setStoredPinning] = useLocalStorage<ColumnPinningState>(
    `table-pinning-${tableId}`,
    defaultPinning,
  );

  const initialPinning = useMemo(
    () => computeInitialPinning(storedPinning, columnOrder, options),
    [storedPinning, columnOrder, options],
  );

  useEffect(() => {
    const update = computePinningUpdate(storedPinning, columnOrder, options);
    if (update) {
      setStoredPinning(update);
    }
  }, [columnOrder, storedPinning, setStoredPinning, options]);

  const onColumnPinningChange = (updater: Updater<ColumnPinningState>) => {
    const updatedPinning = typeof updater === "function" ? updater(storedPinning ?? defaultPinning) : updater;
    setStoredPinning(updatedPinning);
  };

  return {
    columnPinning: initialPinning,
    onColumnPinningChange,
  };
}
